import { memo, useMemo, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';

import { FileUploadDTO } from '@/types/api-types';

import IconDoc from '@/assets/svg/icon-docx.svg?react';
import IconFile from '@/assets/svg/icon-file.svg?react';
import IconHeic from '@/assets/svg/icon-heic.svg?react';
import IconImg from '@/assets/svg/icon-img.svg?react';
import IconVideo from '@/assets/svg/icon-mp4.svg?react';
import IconPdf from '@/assets/svg/icon-pdf.svg?react';

import { getFileExtensionFromString } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckboxSome } from '@/components/ui/checkbox-some';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { ColumnHeaderUploadFiles } from '@/components/FileExplorer/TableColumns';
import UploadSelectionBar from '@/components/UploadFile/UploadSelectionBar';

import { useFiltersUploadStore } from '@/stores/filtersUploadStore';

import { FILE_TYPES, FILE_UPLOAD_STATUS } from '@/constants/global-constants';

import ImageSmallIcon from './components/ImageSmallIcon';

const SKELETON_ROWS_COUNT = 5;

interface UploadFilesTableProps {
  files: FileUploadDTO[];
  setFilesToUpload: (files: FileUploadDTO[]) => void;
  isLoading: boolean;
  onRemove?: (file: FileUploadDTO) => void;
  onView?: (file: FileUploadDTO) => void;
  onViewError?: (file: FileUploadDTO) => void;
}

const getFileIcon = (fileName: string, file?: FileUploadDTO) => {
  const extension = getFileExtensionFromString(fileName);
  const iconMap = {
    img: IconImg,
    pdf: IconPdf,
    video: IconVideo,
    doc: IconDoc,
    docx: IconDoc,
    heic: IconHeic,
    heif: IconHeic,
  };

  const config = Object.values(FILE_TYPES).find(
    (c) =>
      c.mime.split('/')[0].includes(extension) ||
      c.extensions.some((ext) => extension === ext.slice(1)),
  );

  if (
    config?.extensions.some((ext) =>
      FILE_TYPES.IMAGE.extensions.some((imgExt) => imgExt === ext),
    )
  ) {
    return <ImageSmallIcon fileData={file?.file_data ?? new File([], '')} />;
  }

  if (config) {
    const Icon = iconMap[config.icon.type as keyof typeof iconMap];
    return <Icon className={`h-9 w-9 fill-${config.icon.color}`} />;
  }

  return <IconFile className="h-9 w-9 fill-dark-red" />;
};

const getFileDataSignature = (file?: File) =>
  file ? `${file.name}|${file.size}|${file.type}|${file.lastModified}` : '';

const FileIcon = ({ name, file }: { name: string; file?: FileUploadDTO }) => {
  return getFileIcon(name, file);
};

const MemoFileIcon = memo(
  FileIcon,
  (
    prev: Readonly<{ name: string; file?: FileUploadDTO }>,
    next: Readonly<{ name: string; file?: FileUploadDTO }>,
  ) =>
    prev.name === next.name &&
    getFileDataSignature(prev.file?.file_data) ===
      getFileDataSignature(next.file?.file_data),
);

const getUploadFileSignature = (file?: FileUploadDTO) =>
  `${file?.name ?? ''}|${getFileDataSignature(file?.file_data)}`;

const FileTypeCell = memo(
  ({ row, onView }: { row: any; onView?: (file: FileUploadDTO) => void }) => {
    return (
      <div className="flex flex-col items-center">
        <span
          className="text-base font-medium"
          onClick={() => onView?.(row.original)}
        >
          <MemoFileIcon name={row.original.name ?? ''} file={row.original} />
        </span>
      </div>
    );
  },
  (
    prev: Readonly<{ row: any; onView?: (file: FileUploadDTO) => void }>,
    next: Readonly<{ row: any; onView?: (file: FileUploadDTO) => void }>,
  ) =>
    getUploadFileSignature(prev.row?.original) ===
      getUploadFileSignature(next.row?.original) && prev.onView === next.onView,
);

const UploadFilesTable = ({
  files,
  setFilesToUpload,
  isLoading,
  onRemove,
  onView,
  onViewError,
}: UploadFilesTableProps) => {
  const isMobile = useIsMobile();
  const [rowSelection, setRowSelection] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);

  const { sorting, setSorting, filterByIds } = useFiltersUploadStore();

  const selectionColumn: ColumnDef<FileUploadDTO> | null = useMemo(() => {
    if (isMobile) return null;
    return {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center pl-3">
          {table.getIsSomeRowsSelected() ? (
            <CheckboxSome
              checked={table.getIsSomeRowsSelected()}
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ) : (
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          )}
        </div>
      ),
      cell: ({ row }) => (
        <div
          className="flex items-center pl-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      size: 28,
      enableSorting: false,
      enableResizing: false,
    } as ColumnDef<FileUploadDTO>;
  }, [isMobile]);

  const columns: ColumnDef<FileUploadDTO>[] = isMobile
    ? [
        {
          accessorKey: 'name',
          header: () => (
            <div className="flex flex-col font-semibold">Назва</div>
          ),
          size: 200,
          cell: ({ row }) => {
            return (
              <div className="flex flex-col">
                <span className="w-[50vw] truncate text-base font-medium">
                  {row.original.name}
                </span>
              </div>
            );
          },
        },
        {
          accessorKey: 'status',
          header: () => (
            <div className="flex items-center justify-center font-semibold">
              Статус
            </div>
          ),
          size: 80,
          cell: ({ row }) => {
            switch (row.original.status) {
              case FILE_UPLOAD_STATUS.PENDING:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[90px] items-center justify-center rounded-[2px] border border-[#909090] bg-[#E9E9E9]">
                      <span className="text-sm text-[#909090]">
                        Очікує Завант.
                      </span>
                    </div>
                  </div>
                );
              case FILE_UPLOAD_STATUS.UPLOADING:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[90px] items-center justify-center rounded-[2px] border border-[#D77400] bg-[#FBE5D6]">
                      <span className="text-sm text-[#D77400]">В процесі</span>
                    </div>
                  </div>
                );

              case FILE_UPLOAD_STATUS.VALIDATED:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[90px] items-center justify-center rounded-[2px] border border-[#00A72F] bg-[#D8F3D4]">
                      <span className="text-sm text-[#00A72F]">
                        Завантажено
                      </span>
                    </div>
                  </div>
                );

              case FILE_UPLOAD_STATUS.UPLOAD_FAILED:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[90px] items-center justify-center rounded-[2px] border border-dark-red bg-[#FFE6E5]">
                      <span className="text-sm text-dark-red">Помилка</span>
                    </div>
                  </div>
                );

              default:
                return null;
            }
          },
        },
        {
          id: 'actions',
          header: () => <div className="text-right font-semibold">Дія</div>,
          size: 40,
          cell: ({ row }) => {
            return (
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {row.original.file_data?.type.startsWith('image/') && (
                      <DropdownMenuItem onClick={() => onView?.(row.original)}>
                        Переглянути файл
                      </DropdownMenuItem>
                    )}
                    {!!row.original.error && (
                      <DropdownMenuItem
                        onClick={() => onViewError?.(row.original)}
                      >
                        Переглянути помилку
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => onRemove?.(row.original)}
                      className="text-destructive"
                    >
                      Видалити
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
        },
      ]
    : [
        selectionColumn!,
        {
          accessorKey: 'file_type',
          size: 20,
          header: () => (
            <div className="flex flex-col items-center font-semibold">Тип</div>
          ),
          cell: ({ row }) => <FileTypeCell row={row} onView={onView} />,
        },
        {
          accessorKey: 'name',
          header: ({ column }) => (
            <div className="flex flex-col items-center font-semibold">
              <ColumnHeaderUploadFiles
                label="Назва"
                column={column}
                openFilterColumn={openFilterColumn}
                setOpenFilterColumn={setOpenFilterColumn}
                table={table}
              />
            </div>
          ),
          size: 200,
          cell: ({ row }) => {
            return (
              <div className="flex flex-col items-center text-center">
                <span className="w-[200px] truncate text-base font-medium">
                  {row.original.name}
                </span>
              </div>
            );
          },
        },
        {
          accessorKey: 'size',
          header: ({ column }) => (
            <div className="flex flex-col items-center font-semibold">
              <ColumnHeaderUploadFiles
                label="Розмір"
                column={column}
                openFilterColumn={openFilterColumn}
                setOpenFilterColumn={setOpenFilterColumn}
                table={table}
                disabledOptions={true}
              />
            </div>
          ),
          size: 50,
          cell: ({ row }) => {
            return (
              <div className="flex flex-col items-center">
                <span className="text-base font-medium">
                  {row.original.size}
                </span>
              </div>
            );
          },
        },
        {
          accessorKey: 'status',
          header: ({ column }) => (
            <div className="flex flex-col items-center font-semibold">
              <ColumnHeaderUploadFiles
                label="Статус"
                column={column}
                openFilterColumn={openFilterColumn}
                setOpenFilterColumn={setOpenFilterColumn}
                table={table}
                disabledOptions={true}
              />
            </div>
          ),
          size: 80,
          cell: ({ row }) => {
            switch (row.original.status) {
              case FILE_UPLOAD_STATUS.PENDING:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[114px] items-center justify-center rounded-[2px] border border-[#909090] bg-[#E9E9E9]">
                      <span className="text-sm text-[#909090]">
                        Очікує Завант.
                      </span>
                    </div>
                  </div>
                );
              case FILE_UPLOAD_STATUS.UPLOADING:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[114px] items-center justify-center rounded-[2px] border border-[#D77400] bg-[#FBE5D6]">
                      <span className="text-sm text-[#D77400]">В процесі</span>
                    </div>
                  </div>
                );

              case FILE_UPLOAD_STATUS.VALIDATED:
                return (
                  <div className="flex items-center justify-center">
                    <div className="flex h-[20px] w-[114px] items-center justify-center rounded-[2px] border border-[#00A72F] bg-[#D8F3D4]">
                      <span className="text-sm text-[#00A72F]">
                        Завантажено
                      </span>
                    </div>
                  </div>
                );

              case FILE_UPLOAD_STATUS.UPLOAD_FAILED:
                return (
                  <div className="flex items-center justify-center">
                    <button onClick={() => onViewError?.(row.original)}>
                      <div className="flex h-[20px] w-[114px] items-center justify-center rounded-[2px] border border-dark-red bg-[#FFE6E5]">
                        <span className="text-sm text-dark-red">Помилка</span>
                      </div>
                    </button>
                  </div>
                );

              default:
                return null;
            }
          },
        },
        {
          id: 'actions',
          header: () => <div className="text-right font-semibold">Дія</div>,
          size: 40,
          cell: ({ row }) => {
            return (
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onView?.(row.original)}>
                      Переглянути файл
                    </DropdownMenuItem>
                    {!!row.original.error && (
                      <DropdownMenuItem
                        onClick={() => onViewError?.(row.original)}
                      >
                        Переглянути помилку
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => onRemove?.(row.original)}
                      className="text-destructive"
                    >
                      Видалити
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
        },
      ];

  const filesMemoOnFilters = useMemo(() => {
    return files.filter((file) => {
      return Object.entries(filterByIds).every(([key, filter]) => {
        return (
          !filter.length ||
          filter.some((value) => value === file[key as keyof FileUploadDTO])
        );
      });
    });
  }, [files, filterByIds]);

  const table = useReactTable({
    data: filesMemoOnFilters,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => (row.uuid || row.name || '') as string,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    state: {
      sorting,
      rowSelection,
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    filterFns: {
      exact: (row, columnId, value): boolean => {
        const cellValue = String(row.getValue(columnId) || '').toLowerCase();
        const filterValue = String(value).toLowerCase();
        return filterValue === '' || cellValue === filterValue;
      },
    },
  });

  const TableHeader = () => (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} className="z-10 bg-white">
          {headerGroup.headers.map((header) => (
            <th
              key={header.id}
              style={{ width: header.getSize() }}
              className="border-b border-separator pb-4 pt-4 text-left font-semibold text-primary first:pl-3 last:pr-8 last:text-right"
            >
              {!header.isPlaceholder &&
                flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );

  const TableRow = ({ row }: { row: any }) => (
    <tr className="h-[60px] sm:h-[70px]">
      {row.getVisibleCells().map((cell: any) => (
        <td
          key={cell.id}
          className="border-b border-separator text-primary first:pl-3 last:pr-8 last:text-right"
          style={{ width: cell.column.getSize() }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );

  const SkeletonRows = () => (
    <>
      {Array.from({ length: SKELETON_ROWS_COUNT }).map((_, index) => (
        <tr key={`skeleton-${index}`} className="h-[60px] sm:h-[70px]">
          {columns.map((_, cellIndex) => (
            <td
              key={`skeleton-cell-${cellIndex}`}
              className="border-b border-separator first:pl-8 last:pr-8"
            >
              <Skeleton className="h-[30px] w-[80%]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="flex w-full flex-col">
      <UploadSelectionBar table={table} setFilesToUpload={setFilesToUpload} />
      <div className="w-full overflow-auto">
        <table className="w-full border-separate border-spacing-0">
          <TableHeader />
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : files.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-8 text-center text-gray-400"
                >
                  Немає файлів для відображення
                </td>
              </tr>
            ) : (
              table
                .getRowModel()
                .rows.map((row) => <TableRow key={row.id} row={row} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UploadFilesTable;
